import { type ReactNode, useEffect, useState } from 'react'

import { colorTokens } from '~/shared/styles/tokens.config'

import { BackButtonHost } from '../components/BackButton'
import { ViewportRuntime } from '../viewport'
import { detectPlatformKind } from '../detect'
import { createBrowserPlatform } from '../browser'
import { createTelegramPlatform } from '../telegram'
import type { Platform } from '../types'

import { PlatformContext } from './context'

type PlatformProviderProps = {
  children: ReactNode
}

/**
 * Тема у нас всегда тёмная (правило 15), поэтому шапку, фон и нижнюю полосу Telegram красим
 * сами, а не берём цвета из `themeParams` клиента. Значение — из конфига токенов,
 * а не из `getComputedStyle`: Telegram ждёт готовый `#RRGGBB`, и разбирать вычисленный CSS
 * ради того же числа незачем.
 */
const CHROME_COLOR = colorTokens['color-bg']

/**
 * Панель мини-аппы (название приложения и «закрыть») — цветом карточек, а не фона (правка
 * основателя): своя полоса поверх экрана читается приятнее, чем фон, продолжающийся вверх.
 */
const HEADER_COLOR = colorTokens['color-surface']

function createPlatform(): Platform {
  const kind = detectPlatformKind()
  if (kind === 'TELEGRAM' && window.Telegram?.WebApp) {
    return createTelegramPlatform(window.Telegram.WebApp)
  }
  return createBrowserPlatform()
}

/**
 * Создаёт адаптер платформы один раз и кладёт в контекст (docs/telegram.md §2).
 * Порядок в дереве — самый верхний провайдер (см. app/providers/AppProviders).
 */
export function PlatformProvider(props: PlatformProviderProps) {
  const { children } = props
  const [platform] = useState(createPlatform)

  useEffect(() => {
    platform.expand()

    // Свайп вниз по месту без скролла закрывал всю мини-аппу. Штатное лекарство — Bot API 7.7;
    // `overscroll-behavior: none` в global.scss гасит только оттяг внутри скролл-контейнеров
    // и до жеста по нескроллируемому экрану не дотягивается.
    platform.setVerticalSwipesEnabled(false)

    platform.setHeaderColor(HEADER_COLOR)
    platform.setBackgroundColor(CHROME_COLOR)
    platform.setBottomBarColor(CHROME_COLOR)

    // Пункт «Настройки» в меню «…» ведёт в никуда: экран настроек у нас внутри, в профиле.
    platform.hideSettingsButton()
  }, [platform])

  return (
    <PlatformContext.Provider value={platform}>
      {children}
      <BackButtonHost platform={platform} />
      {/* Размеры окна и safe area публикуются CSS-переменными, а не пробрасываются хуком:
          компоненты ui-kit не должны знать про платформу. */}
      <ViewportRuntime platform={platform} />
    </PlatformContext.Provider>
  )
}
